import { toast } from 'sonner';
import { Download, EllipsisVertical, Star, Check } from 'lucide-react';
import { BiCommentDetail } from 'react-icons/bi';
import { Link, useNavigate } from 'react-router-dom';
import { memo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  IMaterialUsed,
  IPostCategory,
  IPostVisibility,
} from '@/redux/post/post.d';
import { cn, getRelativeTime } from '@/lib/utils';
import ProfilePicture from '../ui/profile-picture';
import LikeButton from '@/redux/like/LikeButton';
import OnefinityOfficialIcon from '@/assets/onefinity_officials_badge.png';
import IconTooltip from '@/components/ui/icon-tooltip';
import StoreIcon, {
  dedupeByHost,
  storeHostOf,
  ResolvedLinkHost,
} from '@/components/common/StoreIcon';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useDeletePostDeleteMutation } from '@/redux/post/post.api.slice';
import PostStatusBadge from './PostStatusBadge';
import { IPartnerData } from '@/redux/partner/partner';
import BookmarkButton from '@/redux/bookmark/BookmarkButton';
import RatingButton from '@/redux/projectMake/RatingButton';
import PostMeta from './PostMeta';

interface IPostCardProps {
  id: string;
  authorName: string;
  authorAvatar: string;
  title: string;
  description: string;
  likesCount: number;
  commentsCount: number;
  postThumbnail: string | null;
  downloadCount: number;
  createdAt: string;
  category: IPostCategory;
  hasLikedByUser: boolean | undefined;
  hasBookmarkedByUser: boolean | undefined;
  isOnefinityOfficial: boolean;
  isTrustedMember?: boolean;
  isOwner: boolean;
  visibility?: IPostVisibility;
  authorUsername: string;
  partnerData?: IPartnerData;
  averageTotalRating?: number;
  totalMakes?: number;
  isDimensionalMaterial?: boolean;
  difficulty?: string;
  materialUsed?: IMaterialUsed;
  youtubeUrl?: string | null;
  materialLink?: string | null;
  materialLinks?: string[];
  materialLinkHosts?: ResolvedLinkHost[];
}

function PostCard({
  id,
  authorName,
  authorAvatar,
  title,
  description,
  likesCount,
  commentsCount,
  postThumbnail,
  downloadCount,
  createdAt,
  category,
  hasLikedByUser,
  isOnefinityOfficial,
  isTrustedMember,
  isOwner,
  hasBookmarkedByUser,
  visibility,
  authorUsername,
  partnerData,
  averageTotalRating,
  totalMakes,
  isDimensionalMaterial,
  difficulty,
  materialUsed,
  youtubeUrl,
  materialLink,
  materialLinks,
  materialLinkHosts,
}: IPostCardProps) {
  const navigate = useNavigate();

  const [deletePost] = useDeletePostDeleteMutation();

  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!postToDelete) return;

    try {
      await deletePost(postToDelete).unwrap();
      toast.success('Project deleted');
    } catch (error) {
      toast.error('Failed to delete the project. Please try again.');
    } finally {
      setDeleteDialogOpen(false);
      setPostToDelete(null);
    }
  };

  const confirmDelete = (postId: string) => {
    setPostToDelete(postId);
    setDeleteDialogOpen(true);
  };

  const handleEdit = (postId: string) => {
    navigate(`/project/v2/edit/${postId}`);
  };

  const handleDialogClose = () => {
    setDeleteDialogOpen(false);
    setPostToDelete(null);
  };

  return (
    <>
      <Card className="w-full h-full min-w-0 flex flex-col bg-white border-[1.5px] border-gray-400 overflow-hidden animate-in fade-in duration-200">
        <div className="flex justify-between flex-row items-center p-3">
          <Link to={`/${authorUsername}`}>
            <div className="flex gap-3 items-center">
              <ProfilePicture
                src={authorAvatar}
                alt={authorName}
                username={authorUsername}
                variant="badge"
              />
              <div className="flex flex-col">
                <div className="flex gap-2 items-center">
                  <h6 className="font-primary font-medium">
                    @{authorUsername}
                  </h6>
                  <div className="flex gap-2 items-center">
                    {isOnefinityOfficial ? (
                      <IconTooltip legendKey="onefinity-official">
                        <img
                          src={OnefinityOfficialIcon}
                          className="h-6 w-6"
                          alt="onefinity official"
                        />
                      </IconTooltip>
                    ) : (
                      // eslint-disable-next-line react/jsx-no-useless-fragment
                      <></>
                    )}
                    {isTrustedMember && (
                      <IconTooltip legendKey="trusted-member">
                        <div
                          className="h-5 w-5 rounded-full bg-blue-500 flex items-center justify-center"
                          aria-label="Trusted member"
                        >
                          <Check
                            className="h-3 w-3 text-white"
                            strokeWidth={3}
                          />
                        </div>
                      </IconTooltip>
                    )}
                    {partnerData ? (
                      <IconTooltip
                        legendKey="partner"
                        label={`${partnerData.name ?? 'Partner'} Badge`}
                      >
                        <div
                          className={cn(
                            `h-6 w-6 rounded-full flex items-center justify-center`
                          )}
                          style={{
                            backgroundColor: partnerData?.color,
                          }}
                        >
                          <Star className="h-3 w-3 text-white" fill="white" />
                        </div>
                      </IconTooltip>
                    ) : (
                      // eslint-disable-next-line react/jsx-no-useless-fragment
                      <></>
                    )}
                  </div>
                </div>
                <p className="font-secondary text-[#666666]">
                  {getRelativeTime(createdAt)}
                </p>
              </div>
            </div>
          </Link>
          {isOwner ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <EllipsisVertical />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => handleEdit(id)}>
                  Edit Project
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => confirmDelete(id)}
                  className="focus:text-red-500 focus:bg-red-200"
                >
                  Delete Project
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <BookmarkButton
              bookmarked={hasBookmarkedByUser ?? false}
              postId={id}
              size="icon"
            />
          )}
        </div>
        <div className="flex flex-1 flex-col">
          <Link
            to={`/model/${id}`}
            className="flex flex-col transition-transform duration-150 ease-out-strong active:scale-[0.99]"
          >
            <div className="w-full h-[192px] flex items-center justify-center bg-gray-50 relative overflow-hidden">
              {/* TODO: add a CDN/CloudFront image-transform helper to request
                card-sized width/quality variants and emit `srcset`. No such
                helper exists in the frontend yet, so we keep native lazy
                loading rather than guessing at a URL format. */}
              <img
                src={postThumbnail ?? undefined}
                alt="avatar"
                className="w-full h-full object-contain"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="flex flex-col justify-between p-5 pb-0">
              <div className="flex flex-col justify-between gap-[10px]">
                <div className="flex flex-col gap-[8px]">
                  <div className="flex justify-between items-center gap-2">
                    <div className="flex items-center gap-1.5 flex-1 min-w-0">
                      {isDimensionalMaterial &&
                        dedupeByHost(
                          materialLinks?.length
                            ? materialLinks
                            : [materialLink ?? ''],
                          materialLinkHosts
                        )
                          .filter(Boolean)
                          .map((link) => (
                            <IconTooltip
                              key={link}
                              legendKey="dimensional-material"
                            >
                              <StoreIcon
                                url={link}
                                host={storeHostOf(link, materialLinkHosts)}
                              />
                            </IconTooltip>
                          ))}
                      <h5 className="text-lg font-medium leading-snug font-primary capitalize line-clamp-2 min-w-0">
                        {title}
                      </h5>
                    </div>
                    {visibility ? (
                      <PostStatusBadge
                        visibility={visibility as IPostVisibility}
                      />
                    ) : (
                      <div />
                    )}
                  </div>
                </div>
                <p className="text-sm line-clamp-2 font-tertiary font-normal">
                  {description}
                </p>
              </div>
            </div>
          </Link>

          {/* Outside the Link: the video chip is a real anchor, and an anchor
              cannot nest inside one. `mt-auto` pins the row to the foot so it
              lines up across a grid of uneven descriptions. */}
          <div className="mt-auto px-5 pb-3 pt-[10px]">
            <PostMeta
              skillLevel={difficulty}
              material={materialUsed && String(materialUsed)}
              category={category?.name}
              youtubeUrl={youtubeUrl}
            />
          </div>
        </div>

        <div className="flex justify-around pb-5">
          <LikeButton likes={likesCount} liked={hasLikedByUser} postId={id} />
          <Button variant="outline" size="sm">
            <div className="flex items-center gap-4 justify-center text-[13px]">
              <BiCommentDetail size="16px" />
              <p>{commentsCount}</p>
            </div>
          </Button>
          <RatingButton
            averageTotalRating={averageTotalRating}
            totalMakes={totalMakes}
          />
          <Button variant="outline" size="sm">
            <div className="flex items-center gap-4 justify-center text-[13px]">
              <Download size="16px" />
              <p>{downloadCount}</p>
            </div>
          </Button>
        </div>
      </Card>

      {isDeleteDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-[90%] max-w-md">
            <h2 className="text-lg font-bold mb-4">Confirm Deletion</h2>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this project? This action cannot
              be undone.
            </p>
            <div className="flex justify-end gap-4">
              <Button variant="outline" onClick={handleDialogClose}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Memoized so cards don't re-render when an unrelated parent state (search
// input, sort, newly appended page) changes; props are primitives/stable refs.
export default memo(PostCard);
